import { useEffect, useMemo, useState } from "react";
import { NetworkStatus, useLazyQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import type { Row } from "@/components/Table";
import Button from "@/components/Button";
import ErrorBoundary from "@/components/ErrorBoundary";
import { PaginationSkeleton } from "@/components/Pagination";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { TableFooter } from "@/components/Table";
import type { PageSize, PaginationVariables } from "@/components/Pagination/Pagination";
import { COMMITTEE_ASSIGNMENTS_PAGE_SIZE } from "../constants";
import { BACKOFFICE_COMMITTEE_ASSIGNMENTS_QUERY } from "../queries";
import type {
  BackofficeCommitteeAssignmentsQueryResponse,
  BackofficeCommitteeAssignmentsQueryVariables,
  CommitteeAssignmentStatus,
  CommitteeAssignmentWithCommittee
} from "../types";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { canViewCommitteeAssignmentPerson } from "./TableColumns";
import LocalConnectionPagination from "./LocalConnectionPagination";

const COMMITTEE_ASSIGNMENTS_MOBILE_LAYOUT =
  "[&_.table-mobile_.flex-1]:!px-0 [&_.table-mobile_.flex.w-full>.flex-1.flex.gap-2]:!px-8";

const PANEL_TITLE_BY_STATUS: Record<CommitteeAssignmentStatus, string> = {
  ACTIVE: "Active Participations",
  INACTIVE: "Inactive Participations"
};

interface CommitteeAssignmentsPanelProps {
  committeeId: string;
  committeeName: string;
  participationCount: number;
  status?: CommitteeAssignmentStatus;
  columns: ColumnDef<CommitteeAssignmentWithCommittee, unknown>[];
  onViewParticipant: (participant: CommitteeAssignmentWithCommittee) => void;
}

export default function CommitteeAssignmentsPanel({
  committeeId,
  committeeName,
  participationCount,
  status,
  columns,
  onViewParticipant
}: CommitteeAssignmentsPanelProps) {
  const { isCompactLayout } = useIsCompactLayout();
  const [sorting, setSorting] = useState<SortingState>([{ id: "endDate", desc: true }]);
  const [pageSize, setPageSize] = useState<PageSize>(COMMITTEE_ASSIGNMENTS_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(0);
  const [paginationVariables, setPaginationVariables] = useState<PaginationVariables>({
    first: COMMITTEE_ASSIGNMENTS_PAGE_SIZE
  });

  const [fetchAssignments, { data, loading, called, error, refetch, networkStatus }] = useLazyQuery<
    BackofficeCommitteeAssignmentsQueryResponse,
    BackofficeCommitteeAssignmentsQueryVariables
  >(BACKOFFICE_COMMITTEE_ASSIGNMENTS_QUERY, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    setPageSize(COMMITTEE_ASSIGNMENTS_PAGE_SIZE);
    setCurrentPage(0);
    setPaginationVariables({ first: COMMITTEE_ASSIGNMENTS_PAGE_SIZE });
  }, [committeeId, status]);

  useEffect(() => {
    fetchAssignments({
      variables: {
        id: committeeId,
        ...(status ? { status } : {}),
        first: paginationVariables.first,
        last: paginationVariables.last,
        before: paginationVariables.before,
        after: paginationVariables.after
      }
    });
  }, [
    committeeId,
    status,
    paginationVariables.first,
    paginationVariables.last,
    paginationVariables.before,
    paginationVariables.after,
    fetchAssignments
  ]);

  const assignmentsConnection = data?.backofficeCommittee?.assignments;
  const assignments = useMemo(
    (): CommitteeAssignmentWithCommittee[] =>
      (assignmentsConnection?.nodes ?? []).map((assignment) => ({
        ...assignment,
        committeeName
      })),
    [committeeName, assignmentsConnection?.nodes]
  );

  const assignmentsTotalCount = assignmentsConnection?.totalCount ?? participationCount;
  const panelTitle = `${status ? PANEL_TITLE_BY_STATUS[status] : "Participations"} (${assignmentsTotalCount})`;
  const pageInfo = assignmentsConnection?.pageInfo ?? {
    hasNextPage: false,
    hasPreviousPage: false
  };
  const isPaging = networkStatus === NetworkStatus.fetchMore || networkStatus === NetworkStatus.setVariables;
  const panelLoading = !called || loading || isPaging || (!assignmentsConnection && !error);

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((currentSorting) =>
      typeof updaterOrValue === "function" ? updaterOrValue(currentSorting) : updaterOrValue
    );
  };

  const renderFooter =
    assignmentsTotalCount > 0
      ? () =>
          panelLoading ? (
            <TableFooter pagination={<PaginationSkeleton />} />
          ) : (
            <LocalConnectionPagination
              pageInfo={pageInfo}
              setVariables={setPaginationVariables}
              pageSize={pageSize}
              setPageSize={setPageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageRows={assignments.length}
              totalRows={assignmentsTotalCount}
            />
          )
      : undefined;

  return (
    <div className={COMMITTEE_ASSIGNMENTS_MOBILE_LAYOUT}>
      {error && called && !panelLoading ? (
        <>
          {isCompactLayout && <h3 className="text-title-small px-4 py-4">{panelTitle}</h3>}
          <div className="border-t border-sys-brand-surface-container-highest bg-sys-brand-surface-container px-4 py-8">
            <ErrorBoundary
              variant="inline"
              title="Unable to load participations"
              message={error.message}
              action={
                <Button variant="outlined" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            />
          </div>
        </>
      ) : (
        <TableDetailsPanel
          data={assignments}
          columns={columns}
          title={panelTitle}
          loading={panelLoading}
          sortingState={sorting}
          onSortingChange={handleSortingChange}
          renderFooter={renderFooter}
          onClickRow={(row: Row<CommitteeAssignmentWithCommittee>) => {
            if (canViewCommitteeAssignmentPerson(row.original)) {
              onViewParticipant(row.original);
            }
          }}
          getRowClasses={(row) => (canViewCommitteeAssignmentPerson(row.original) ? "" : "cursor-default")}
        />
      )}
    </div>
  );
}
